import { usePublish } from "@booking/hooks/usePubSub";
import { gaClickAttrs, type GaClickParams } from "@booking/utils/tracking";
import clsx from "clsx";
import React from "react";

import type { CheckboxElementProps, CommonProps } from "@/types";
import { createId } from "@/utils/helpers";

import styles from "./CheckboxInput.module.css";

const ref = "checkbox-input";

enum CheckboxInputEventEnum {
  CHANGE = "change",
}

enum CheckboxVariantEnum {
  NATIVE = "native",
  TOGGLE = "toggle",
}

interface CheckboxInputProps
  extends Partial<CommonProps & CheckboxElementProps & GaClickParams> {
  labelOnly?: boolean;
  noBorder?: boolean;
  rounded?: boolean;
  variant?: CheckboxVariantEnum;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
}

function useCheckboxInput(props: CheckboxInputProps) {
  const {
    color,
    disabled,
    id = createId(ref),
    invalid,
    value = "",
    variant = CheckboxVariantEnum.NATIVE,
  } = props;
  const className = clsx(
    styles[ref],
    color && styles[color],
    disabled && styles.disabled,
    invalid && styles.invalid,
    props.labelOnly && styles["label-only"],
    props.noBorder && styles["no-border"],
    props.rounded && styles.rounded,
    variant !== CheckboxVariantEnum.NATIVE && styles[variant],
    props.className,
  );

  return {
    className,
    disabled: !!disabled,
    id,
    invalid: !!invalid,
    value: value.length > 0 ? value : undefined,
  };
}

function CheckboxInput(props: CheckboxInputProps) {
  const { name, click = `${name}_click` } = props;
  const { className, disabled, id, value } = useCheckboxInput(props);
  const publish = usePublish({ context: name, ...props.pubSubData });

  function onChange(e: React.ChangeEvent<HTMLInputElement>) {
    publish(CheckboxInputEventEnum.CHANGE, { value: e.target.checked });

    props.onChange && props.onChange(e);
  }

  return (
    <label
      className={className}
      htmlFor={id}
      {...gaClickAttrs({
        click,
        clickTarget: props.clickTarget,
        eventGroup: props.eventGroup,
      })}
    >
      <input
        defaultChecked={props.checked}
        disabled={disabled}
        id={id}
        name={name}
        type={"checkbox"}
        value={value}
        onChange={onChange}
      />
      {props.children}
    </label>
  );
}

function ManagedCheckboxInput(props: CheckboxInputProps) {
  const { name, click = `${name}_click` } = props;
  const { className, disabled, id, value } = useCheckboxInput(props);
  const publish = usePublish({ context: name, ...props.pubSubData });

  function onChange(e: React.ChangeEvent<HTMLInputElement>) {
    publish(CheckboxInputEventEnum.CHANGE, { value: e.target.checked });
  }

  return (
    <label
      className={className}
      htmlFor={id}
      {...gaClickAttrs({
        click,
        clickTarget: props.clickTarget,
        eventGroup: props.eventGroup,
      })}
    >
      <input
        checked={props.checked}
        disabled={disabled}
        id={id}
        name={name}
        type={"checkbox"}
        value={value}
        onChange={onChange}
      />
      {props.children}
    </label>
  );
}

export default CheckboxInput;
export { CheckboxInput, type CheckboxInputProps };
export { CheckboxInputEventEnum, CheckboxVariantEnum };
export { ManagedCheckboxInput };
